import React, { useState } from 'react';
import { useOasisBackend } from "../../hooks/useOasisBackend";
import { Entity } from "../../api/OasisBackendApi";

interface AddQueryToEntityProps {
  entity: Entity;
  entityCollectionId: string;
  buttonText?: string;
  buttonClassName?: string;
}

interface QueryForm {
  text: string;
  correct_answer_entity_ids: string[];
}

const AddQueryToEntity: React.FC<AddQueryToEntityProps> = ({
  entity,
  entityCollectionId,
  buttonText = "Add Query",
  buttonClassName = "text-blue-400 hover:text-blue-300 bg-blue-900/20 hover:bg-blue-900/40 border border-blue-700/50 hover:border-blue-600/70 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200"
}) => {
  const db = useOasisBackend();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [queries, setQueries] = useState<QueryForm[]>([
    { text: '', correct_answer_entity_ids: [entity.id] }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddQuery = () => {
    setQueries([...queries, { text: '', correct_answer_entity_ids: [entity.id] }]);
  };

  const handleRemoveQuery = (index: number) => {
    if (queries.length > 1) {
      setQueries(queries.filter((_, i) => i !== index));
    }
  };

  const handleQueryChange = (index: number, text: string) => {
    const updatedQueries = [...queries];
    updatedQueries[index].text = text;
    setQueries(updatedQueries);
  };

  const handleSave = async () => {
    // Filter out empty queries
    const validQueries = queries.filter(query => query.text.trim() !== '');
    
    if (validQueries.length === 0) {
      setError("Please add at least one query");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create queries one by one
      // for (const query of validQueries) {
      //   await db.endpoints.api.apiEntityCollectionQueriesCreate(entityCollectionId, {
      //     text: query.text.trim(),
      //     correct_answer_entity_ids: query.correct_answer_entity_ids
      //   });
      // }

      // Reset form and close modal
      setQueries([{ text: '', correct_answer_entity_ids: [entity.id] }]);
      setIsModalOpen(false);
    } catch (err) {
      setError("Failed to create queries. Please try again.");
      console.error("Error creating queries:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setQueries([{ text: '', correct_answer_entity_ids: [entity.id] }]);
    setError(null);
  };

  // Extract entity content for display
  const entityContent = typeof entity.content === 'object' ? entity.content : {};
  const entityKeys = Object.keys(entityContent);

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={buttonClassName}
      >
        {buttonText}
      </button>

      {isModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="bg-gray-900 text-white" style={{
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto',
            width: '90%'
          }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Create Queries for Entity</h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-600 text-white rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-6">
              {/* Entity Information */}
              <div className="bg-gray-800 p-4 rounded-md border border-gray-700">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                  Entity Details
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  This entity will be used as the correct answer for all queries you create below. 
                  Review the entity details to help you write relevant queries.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {entityKeys.map(key => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        {key}
                      </label>
                      <div className="text-sm text-gray-200 bg-gray-700 p-2 rounded border border-gray-600">
                        {String(entityContent[key])}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Queries Section */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    Queries
                  </h3>
                  <button
                    onClick={handleAddQuery}
                    className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium transition-colors duration-200 flex items-center gap-1"
                  >
                    <span className="text-lg">+</span>
                    Add Query
                  </button>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  Create queries that would help users find this entity. You can add multiple queries to cover different ways users might search for this entity.
                </p>

                <div className="space-y-4">
                  {queries.map((query, index) => (
                    <div key={index} className="bg-gray-800 p-4 rounded-md border border-gray-700">
                      <div className="flex justify-between items-start mb-3">
                        <label className="block text-sm font-medium text-gray-300 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                          Query {index + 1}
                        </label>
                        {queries.length > 1 && (
                          <button
                            onClick={() => handleRemoveQuery(index)}
                            className="text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded hover:bg-red-900/20 transition-colors duration-200"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <textarea
                        value={query.text}
                        onChange={(e) => handleQueryChange(index, e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        rows={3}
                        placeholder="Example: What is the capital of France? or Who wrote Romeo and Juliet?"
                      />
                      <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                        <span className="w-1 h-1 bg-green-400 rounded-full"></span>
                        This query will have the selected entity as the correct answer.
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Queries'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddQueryToEntity;